import { Dayjs } from 'dayjs';
import { Buffer } from "buffer";
//
import { badWords } from './badWords';
import config from 'config';
import i18n from 'i18n';
import levenshtein from 'fast-levenshtein';

export const buildUserName = (user:any) => {
  if (!user) return '';
  return user.username ? user.username : `${user.name} ${user.surname}`;
};

export const getTimeRemaining = (start:Dayjs, end:Dayjs) => {
  const diff = end.diff(start);
  const seconds = Math.floor((diff/1000) % 60);
  const minutes = Math.floor((diff/1000/60) % 60);
  const hours = Math.floor((diff/(1000*60*60)) % 24);
  const days = Math.floor(diff/(1000*60*60*24));
  return { days, hours, minutes, seconds };
};

export const decodeBase64 = (hash:string) => {
  const hasToString = Buffer.from(hash, 'base64').toString();
  return JSON.parse(hasToString);
}

export const getSearchParam = (search:string, field:string):string|null => {
  const searchParams = new URLSearchParams(search);
  return searchParams.get(field) || null;
}

export const isNumeric = (value:string):boolean => {
  return /^\d+$/.test(value);
}

export const checkIsProfanity = (value:string):boolean => {
  if (!value) return false;

  for (const word of badWords) {
    if (value.trim().toLowerCase().includes(word)) {
      return true;
    }
  }

  return false;
}

export const fetchWithAutoRetry = async (fetcher:Function, data:any = null) => {
  return await new Promise((resolve, reject) => {
    let retries = 0;
    const caller = () => {
      fetcher(data)
        .then((data:any) => {
          resolve(data)
        })
        .catch((error:any) => {
          if (error.status !== 502) {
            reject(error);
          } else if (retries < config.autoRetryFetch.count) {
            retries++;
            setTimeout(() => {
              caller();
            }, config.autoRetryFetch.timeout);
          } else {
            reject(error);
          }
        });
    }
    retries = 1;
    caller();
  })
}

export const getEndOfPlace = (place: number): string => {
  const lng = i18n.language;

  switch (lng) {
    case 'en': {
      const suffixes: { [key: number]: string } = { 1: 'st', 2: 'nd', 3: 'rd' };
      const lastDigit = place % 10;
      const lastTwoDigits = place % 100;
      if (lastTwoDigits >= 11 && lastTwoDigits <= 13) return 'th';
      return suffixes[lastDigit] || 'th';
    }

    case 'es': {
      if ([4, 5, 6].includes(place)) return 'to';
      if ([7, 10].includes(place)) return 'mo';
      if (place === 8) return 'vo';
      if (place === 9) return 'no';
      return 'º';
    }

    case 'se': {
      return ':e';
    }

    case 'pt': {
      return 'º';
    }

    case 'ar': {
      return ' -الأول';
    }

    case 'hi': {
      return 'वाँ';
    }

    case 'ga': {
      return 'ú';
    }

    default:
      return '';
  }
};

 export const getIOSVersion = (): number | null => {
  const ua = navigator.userAgent;
  const isIOS = /iP(hone|od|ad)/.test(ua);
  if (!isIOS) return null;

  const match = ua.match(/OS (\d+)_/);
  return match ? parseInt(match[1], 10) : null;
}

export const renderWithVariables = (
  template: string,
  variables: Record<string, string | undefined>
): string => {
  return template.replace(/{{\s*(\w+)\s*}}/g, (_, key) => variables[key] ?? '');
};

export const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = "__test__";
    window.localStorage.setItem(testKey, "1");
    window.localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
}

export const textFromNormalToCamelCase = (text: string) => {
  return text
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase());
};

export const shuffle = <T,>(array: T[]): T[] =>
  [...array].sort(() => Math.random() - 0.5);

export const teamSelectorFormations = {
  '4-4-2': [
    { role: 'GK', label: 'Goalkeeper', x: 50, y: 90 },

    { role: 'RB', label: 'Right Back', x: 80, y: 60 },
    { role: 'CB1', label: 'Right Center Back', x: 70, y: 75 },
    { role: 'CB2', label: 'Left Center Back', x: 30, y: 75 },
    { role: 'LB', label: 'Left Back', x: 20, y: 60 },

    { role: 'RM', label: 'Right Midfielder', x: 80, y: 30 },
    { role: 'CM1', label: 'Right Central Midfielder', x: 70, y: 45 },
    { role: 'CM2', label: 'Left Central Midfielder', x: 30, y: 45 },
    { role: 'LM', label: 'Left Midfielder', x: 20, y: 30 },

    { role: 'ST1', label: 'First Striker', x: 70, y: 15 },
    { role: 'ST2', label: 'Second Striker', x: 30, y: 15 },
  ],

  '4-5-1': [
    { role: 'GK', label: 'Goalkeeper', x: 50, y: 90 },

    { role: 'RB', label: 'Right Back', x: 80, y: 60 },
    { role: 'CB1', label: 'Right Center Back', x: 70, y: 75 },
    { role: 'CB2', label: 'Left Center Back', x: 30, y: 75 },
    { role: 'LB', label: 'Left Back', x: 20, y: 60 },

    { role: 'RM', label: 'Right Midfielder', x: 80, y: 30 },
    { role: 'CM1', label: 'Right Central Midfielder', x: 70, y: 45 },
    { role: 'CM2', label: 'Left Central Midfielder', x: 30, y: 45 },
    { role: 'CAM', label: 'Attacking Midfielder', x: 50, y: 25 },
    { role: 'LM', label: 'Left Midfielder', x: 20, y: 30 },

    { role: 'ST', label: 'Striker', x: 50, y: 10 },
  ],

  '3-4-3': [
    { role: 'GK', label: 'Goalkeeper', x: 50, y: 90 },

    { role: 'CB1', label: 'Right Center Back', x: 80, y: 65 },
    { role: 'CB2', label: 'Middle Center Back', x: 50, y: 75 },
    { role: 'CB3', label: 'Left Center Back', x: 20, y: 65 },

    { role: 'RM', label: 'Right Midfielder', x: 80, y: 50 },
    { role: 'CM1', label: 'Right Central Midfielder', x: 70, y: 35 },
    { role: 'CM2', label: 'Left Central Midfielder', x: 30, y: 35 },
    { role: 'LM', label: 'Left Midfielder', x: 20, y: 50 },

    { role: 'RW', label: 'Right Winger', x: 75, y: 20 },
    { role: 'ST', label: 'Striker', x: 50, y: 10 },
    { role: 'LW', label: 'Left Winger', x: 25, y: 20 },
  ]
};

export const isCorrectAnswer = (userAnswer: string, correctAnswers: string[], maxDistance:number = 2): boolean => {
  const normalizedUserAnswer = userAnswer.trim().toLowerCase();

  return correctAnswers.some(answer => {
    const normalizedAnswer = answer.trim().toLowerCase();
    const distance = levenshtein.get(normalizedAnswer, normalizedUserAnswer);
    return distance <= maxDistance;
  });
};
